/**
 * RadioQuestion Component
 * Displays radio button options in a bento box grid layout
 * Supports optional images for visual selection
 */

import { QuestionOption } from '../../../types/formConfig'
import imageRegistry from '../../../config/imageRegistry'

interface RadioQuestionProps {
  questionId: string
  options: QuestionOption[]
  value: string
  onChange: (value: string) => void
  required?: boolean
}

const RadioQuestion = ({
  questionId,
  options,
  value,
  onChange,
  required = false
}: RadioQuestionProps) => {
  const handleOptionClick = (optionValue: string) => {
    onChange(optionValue)
  }

  const handleKeyDown = (e: React.KeyboardEvent, optionValue: string) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault()
      onChange(optionValue)
    }
  }

  // Determine grid columns based on number of options
  const getGridCols = () => {
    if (options.length === 2) return 'grid-cols-1 md:grid-cols-2'
    if (options.length === 3) return 'grid-cols-1 md:grid-cols-3'
    if (options.length === 4) return 'grid-cols-2 md:grid-cols-4'
    return 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3'
  }

  return (
    <div className="w-full">
      <div className={`grid ${getGridCols()} gap-4`}>
        {options.map((option) => {
          const isSelected = value === option.value
          const resolvedImage = option.image
            ? imageRegistry[option.image] ?? option.image
            : undefined
          const hasImage = !!resolvedImage

          return (
            <div
              key={option.value}
              onClick={() => handleOptionClick(option.value)}
              onKeyDown={(e) => handleKeyDown(e, option.value)}
              role="radio"
              aria-checked={isSelected}
              aria-required={required}
              tabIndex={0}
              className={`
                relative cursor-pointer rounded-xl border-2 p-6 transition-all duration-200
                ${
                  isSelected
                    ? 'border-primary-green bg-primary-green bg-opacity-5 shadow-lg scale-105'
                    : 'border-gray-200 bg-white hover:border-primary-green hover:shadow-md'
                }
                focus:outline-none focus:ring-2 focus:ring-primary-green focus:ring-opacity-50
              `}
            >
              {/* Selection Checkmark */}
              {isSelected && (
                <div className="absolute top-4 right-4 w-6 h-6 bg-primary-green rounded-full flex items-center justify-center">
                  <svg
                    className="w-4 h-4 text-white"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="3"
                      d="M5 13l4 4L19 7"
                    />
                  </svg>
                </div>
              )}

              {/* Option Content */}
              <div className="flex flex-col items-center text-center">
                {/* Image (if provided) */}
                {hasImage && (
                  <div className="mb-4 w-20 h-20 flex items-center justify-center">
                    <img
                      src={resolvedImage}
                      alt={option.label}
                      className="w-full h-full object-contain"
                      onError={(e) => {
                        // Fallback if image fails to load - show placeholder
                        const target = e.target as HTMLImageElement
                        target.style.display = 'none'
                        const placeholder = target.nextElementSibling as HTMLElement
                        if (placeholder) {
                          placeholder.style.display = 'flex'
                        }
                      }}
                    />
                    {/* Placeholder fallback */}
                    <div className="hidden w-20 h-20 bg-gray-100 rounded-lg items-center justify-center">
                      <svg
                        className="w-10 h-10 text-gray-400"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="2"
                          d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                        />
                      </svg>
                    </div>
                  </div>
                )}

                {/* Label */}
                <h3
                  className={`font-semibold text-lg mb-2 ${
                    isSelected ? 'text-primary-green' : 'text-dark-plum'
                  }`}
                >
                  {option.label}
                </h3>

                {/* Description (if provided) */}
                {option.description && (
                  <p className="text-sm text-gray-600 leading-relaxed">
                    {option.description}
                  </p>
                )}
              </div>

              {/* Hidden radio input for form compatibility */}
              <input
                type="radio"
                name={questionId}
                value={option.value}
                checked={isSelected}
                onChange={() => onChange(option.value)}
                className="sr-only"
                aria-hidden="true"
                tabIndex={-1}
              />
            </div>
          )
        })}
      </div>
    </div>
  )
}

export default RadioQuestion
